import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useAuth } from '../../context/AuthContext'
import AlphabetDrill from '../../components/learners/AlphabetDrill'
import { useInsufficientCreditsAlert } from '../../components/InsufficientCreditsAlert'
import useCreoleAudio from '../../hooks/useCreoleAudio'
import LanguageSwitcher from '../../components/LanguageSwitcher'
import { useLanguage } from '../../context/LanguageContext'
import LEARNING_COPY from '../../locales/learning'

export default function AlphabetPage() {
  const router = useRouter()
  const { language } = useLanguage()
  const t = LEARNING_COPY[language] || LEARNING_COPY.en
  const { isAuthenticated, authToken, loading: authLoading } = useAuth()
  const [letters, setLetters] = useState([])
  const [loading, setLoading] = useState(true)
  const { showInsufficientCreditsAlert, AlertComponent } = useInsufficientCreditsAlert()
  const { handlePlayAudio: playEntryAudio } = useCreoleAudio({
    channel: 'learner-alphabet-pronunciation',
    onInsufficientCredits: showInsufficientCreditsAlert,
  })

  useEffect(() => {
    if (authLoading) return
    if (!isAuthenticated) {
      router.push('/login')
      return
    }
    if (authToken) {
      fetchAlphabet()
    }
  }, [isAuthenticated, authToken, authLoading])

  const fetchAlphabet = async () => {
    try {
      const api = (await import('../../services/api')).default
      const res = await api.get('/v1/learners/alphabet/drill/')
      setLetters(res.data.letters)
    } catch (err) {
      console.error('Failed to fetch alphabet:', err)
    }
    setLoading(false)
  }

  const handlePlayAudio = (letter) => {
    const entryId = letter.entry_id
    if (!entryId) {
      console.error('No dictionary entry_id for letter:', letter.letter)
      return
    }
    return playEntryAudio(entryId, { cachedAudioUrl: letter.audio_url || undefined })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="relative min-h-screen bg-gray-50 dark:bg-gray-900">
      <LanguageSwitcher variant="top-right" className="!right-11" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => router.push('/learn')}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t.alphabet_title}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t.cat_creole_alphabet_desc}
            </p>
          </div>
        </div>

        <AlphabetDrill letters={letters} onPlayAudio={handlePlayAudio} />
      </div>

      <AlertComponent />
    </div>
  )
}
